import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TabBar from '@/components/feature/TabBar';
import { getRush, submitRush } from '@/api';
import type { RushData } from '@/api';

function pad(n: number) {
  return String(n).padStart(2, '0');
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export default function Purchase() {
  const navigate = useNavigate();
  const [data, setData] = useState<RushData | null>(null);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [target, setTarget] = useState<Date | null>(null);
  const [toast, setToast] = useState('');
  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(''), 2500);
  }, []);

  const loadRush = useCallback(async () => {
    try {
      const res = await getRush();
      setData(res);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    }
  }, []);

  useEffect(() => {
    loadRush();
  }, [loadRush]);

  //倒计时：基于服务端返回的场次开始时间；无场次（nextStartAt=0）不轮询
  const refreshedTargetRef = useRef<number | null>(null);
  useEffect(() => {
    if (!data) return;
    const hasTarget = data.nextStartAt > 0;
    const targetMs = hasTarget ? data.nextStartAt * 1000 : 0;
    setTarget(hasTarget ? new Date(targetMs) : null);

    const calc = (): TimeLeft => {
      const diff = targetMs - Date.now();
      if (!hasTarget || diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calc());
    if (!hasTarget) return;

    const timer = setInterval(() => {
      setTimeLeft(calc());
      //倒计时归零后仅对同一目标时间刷新一次，等服务端更新 nextStartAt 后才允许下次刷新
      if (Date.now() >= targetMs && refreshedTargetRef.current !== data.nextStartAt) {
        refreshedTargetRef.current = data.nextStartAt;
        loadRush();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [data, loadRush]);

  //抢单提交（抢挂卖单）
  const handleGrab = useCallback(async (orderId: number) => {
    if (!data || !data.currentSlotId) return;
    if (submittingId) return;
    setSubmittingId(orderId);
    try {
      const res = await submitRush(orderId, data.currentSlotId);
      showToast(`抢单成功！订单号 ${res.orderNo}，金额 ¥${Number(res.amount).toFixed(2)}`);
      loadRush();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '抢单失败');
    } finally {
      setSubmittingId(null);
    }
  }, [data, submittingId, showToast, loadRush]);

  const currentSlot = data?.slots.find((s) => s.id === data?.currentSlotId) || null;
  const nextSlot = data?.slots.find((s) => s.state === 3) || null;
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const targetDateStr = target
    ? `${target.getMonth() + 1}月${target.getDate()}日${weekdays[target.getDay()]} ${pad(target.getHours())}:${pad(target.getMinutes())} 进货`
    : '';
  const records = data?.todayRecords || [];
  const quota = data?.quota;
  const listings = data?.listings || [];
  const canGrab = !!currentSlot && quota ? quota.unlimited || quota.morningCount < quota.morningQuota : false;

  return (
    <div className="mobile-container pb-20">
      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-foreground-800 text-white text-sm rounded-full shadow-lg max-w-[85vw] text-center">
          {toast}
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="flex items-center px-3 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center text-foreground-800 cursor-pointer"
            aria-label="返回"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <h1 className="flex-1 text-center text-foreground-900 text-base font-bold tracking-wide">
            限时进货
          </h1>
          <div className="flex items-center gap-1 text-foreground-400 text-xs">
            <i className="ri-fire-line text-sm text-orange-500" />
            {currentSlot ? '抢购进行中' : '暂未开放'}
          </div>
        </div>
      </header>

      <main className="bg-background-100 px-3 py-4 space-y-4">
        {/* 加载/失败 */}
        {!data ? (
          <div className="flex flex-col items-center justify-center py-24">
            {error ? (
              <>
                <i className="ri-wifi-off-line text-foreground-300 text-4xl mb-3" />
                <p className="text-foreground-400 text-sm">{error}</p>
              </>
            ) : (
              <>
                <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
                <p className="text-foreground-400 text-sm">加载中...</p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Today's record */}
            <div className="bg-white rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="ri-file-list-3-line text-foreground-500 text-lg" />
                  <span className="text-foreground-900 font-semibold text-sm">今日进货记录</span>
                </div>
                <span className="px-2.5 py-0.5 text-[10px] font-medium text-primary-500 bg-primary-50 rounded-full border border-primary-100">
                  {records.length}单
                </span>
              </div>
              {records.length === 0 ? (
                <div className="flex items-center gap-2 text-foreground-400 text-xs">
                  <i className="ri-time-line text-sm" />
                  <span>进货开始后，您的进货将在此展示，请耐心等待</span>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {records.map((r) => (
                    <div key={r.id} className="flex items-center gap-3 cursor-pointer active:opacity-70 transition-opacity" onClick={() => navigate("/buyer-warehouse")}>
                      <div className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {r.productImage ? (
                          <img src={r.productImage} alt={r.productName} className="w-full h-full object-cover" />
                        ) : (
                          <i className="ri-image-line text-foreground-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground-800 text-xs font-medium truncate">{r.productName}</p>
                        <p className="text-foreground-400 text-[10px] mt-0.5">
                          {r.orderNo} · {r.time}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-foreground-900 text-xs font-semibold">¥{Number(r.amount).toFixed(2)}</p>
                        <p className="text-primary-500 text-[10px] mt-0.5">{r.statusText}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Countdown card */}
            <div className="relative rounded-2xl overflow-hidden">
              {/* Gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 via-primary-500 to-primary-600" />

              <div className="relative p-5 text-white">
                {/* Next purchase label */}
                <div className="flex items-center justify-center gap-1.5 mb-3">
                  <i className="ri-time-line text-base" />
                  <span className="text-sm font-medium">{currentSlot ? '当前场次进行中' : '下次进货'}</span>
                </div>

                {/* Title */}
                <p className="text-center text-white/90 text-sm mb-4">
                  {currentSlot ? `距${currentSlot.name}结束还有` : '距进货还有'}
                </p>

                {/* Countdown digits */}
                <div className="flex items-center justify-center gap-3 mb-5">
                  {[
                    { value: timeLeft.days, label: '天' },
                    { value: timeLeft.hours, label: '时' },
                    { value: timeLeft.minutes, label: '分' },
                    { value: timeLeft.seconds, label: '秒' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-1">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <span className="text-2xl font-bold font-mono tracking-wider">
                          {pad(item.value)}
                        </span>
                      </div>
                      <span className="text-xs text-white/80">{item.label}</span>
                    </div>
                  ))}
                </div>

                {/* Date pill */}
                {!currentSlot && targetDateStr && (
                  <div className="flex justify-center mb-2">
                    <div className="flex items-center gap-1.5 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                      <i className="ri-calendar-line text-xs" />
                      <span>{targetDateStr}</span>
                    </div>
                  </div>
                )}

                {/* Session info */}
                <p className="text-center text-white/80 text-xs mb-4">
                  {currentSlot
                    ? `${currentSlot.name} · ${currentSlot.startTime}-${currentSlot.endTime}`
                    : nextSlot
                      ? `${nextSlot.name} · ${nextSlot.startTime}-${nextSlot.endTime}`
                      : '暂无场次安排'}
                </p>

                {/* Quota */}
                {quota && (
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center gap-1.5 px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-xs">
                      <i className="ri-flashlight-line text-xs" />
                      <span>
                        {quota.unlimited
                          ? '今日额度 不限量 · 老板'
                          : `今日额度 ${quota.morningCount}/${quota.morningQuota}${quota.userType === 2 ? ' · 正式期' : ' · 体验期'}`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Status button */}
                <div className="flex justify-center">
                  <div className="flex items-center gap-1.5 px-5 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                    <i className={`${currentSlot ? 'ri-flashlight-fill' : 'ri-flashlight-line'} text-xs`} />
                    <span>
                      {currentSlot
                        ? canGrab
                          ? '场次进行中，立即抢购下方商品'
                          : '今日额度已用完'
                        : '活动未开始，请耐心等待'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 可抢挂卖单（当前场次进行中时展示） */}
            {currentSlot && (
              <div className="bg-white rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <i className="ri-shopping-bag-3-line text-foreground-500 text-lg" />
                    <span className="text-foreground-900 font-semibold text-sm">可抢挂卖单</span>
                  </div>
                  <span className="text-[10px] text-foreground-400">{currentSlot.name}</span>
                </div>
                {listings.length === 0 ? (
                  <div className="flex items-center justify-center gap-2 py-8 text-foreground-400 text-xs">
                    <i className="ri-inbox-line" />
                    <span>暂无可抢挂卖单</span>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {listings.map((p) => (
                      <div key={p.id} className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {p.image ? (
                            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <i className="ri-image-line text-foreground-300 text-lg" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground-800 text-xs font-medium truncate">{p.name}</p>
                          <p className="text-[10px] text-foreground-400 mt-0.5 truncate">
                            卖家：{p.sellerName || '平台'}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0 mr-1">
                          <p className="text-primary-500 text-sm font-bold">¥{Number(p.price).toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => handleGrab(p.id)}
                          disabled={!canGrab || submittingId === p.id}
                          className="px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-primary-500 text-white text-xs font-medium cursor-pointer hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 flex-shrink-0"
                        >
                          {submittingId === p.id ? (
                            <i className="ri-loader-4-line animate-spin text-sm" />
                          ) : (
                            <i className="ri-flashlight-line text-sm" />
                          )}
                          抢购
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 时段列表 */}
            {data.slots.length > 0 && (
              <div className="bg-white rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-schedule-line text-foreground-500 text-lg" />
                  <span className="text-foreground-900 font-semibold text-sm">今日场次</span>
                </div>
                <div className="space-y-2">
                  {data.slots.map((s) => (
                    <div
                      key={s.id}
                      className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${
                        s.state === 1
                          ? 'border-primary-200 bg-primary-50'
                          : 'border-background-100 bg-background-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-foreground-800 text-xs font-medium">{s.name}</span>
                        <span className="text-foreground-400 text-[10px]">{s.startTime}-{s.endTime}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            s.state === 1
                              ? 'bg-primary-500 text-white'
                              : s.state === 2
                                ? 'bg-background-200 text-foreground-400'
                                : 'bg-amber-50 text-amber-600 border border-amber-200'
                          }`}
                        >
                          {s.state === 1 ? '进行中' : s.state === 2 ? '已结束' : '未开始'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <TabBar />
    </div>
  );
}
